<template>
  <div class="company-management">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>企业管理</span>
          <el-button type="primary" @click="handleCreate">新增企业</el-button>
        </div>
      </template>

      <el-alert type="info" show-icon :closable="false" class="company-sea-alert">
        <template #title>公海按天自动回收说明</template>
        <div class="company-sea-alert-body">
          每个企业在「公海配置」中分别设置「自建/导入」「公海认领」未跟进天数；执行时按企业读取
          <code>company</code> 表配置，互不影响。
          实际<strong>扫描触发时间</strong>由「定时任务管理」中任务
          <code>CRM_PUBLIC_SEA_AUTO_RECYCLE</code>（类
          <code>com.zx.job.CustomerCrmMaintenanceJob</code>，方法
          <code>executePublicSeaAutoRecycle</code>）的 Cron 决定。
          <el-link type="primary" @click="goScheduledTaskPage">前往定时任务管理</el-link>
        </div>
      </el-alert>

      <!-- 搜索表单 -->
      <el-form :inline="true" :model="queryParams" class="search-form">
        <el-form-item label="企业名称">
          <el-input
            v-model="queryParams.companyName"
            placeholder="请输入企业名称"
            clearable
            @keyup.enter="handleSearch"
            style="width: 200px"
          />
        </el-form-item>
        <el-form-item label="企业编码">
          <el-input
            v-model="queryParams.companyCode"
            placeholder="请输入企业编码"
            clearable
            @keyup.enter="handleSearch"
            style="width: 200px"
          />
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="queryParams.status" placeholder="全部" clearable style="width: 120px">
            <el-option label="启用" :value="1" />
            <el-option label="禁用" :value="0" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 企业列表 -->
      <el-table
        v-loading="loading"
        :data="companyList"
        border
        style="width: 100%"
      >
        <el-table-column prop="id" label="ID" width="60" />
        <el-table-column prop="companyCode" label="企业编码" width="120" />
        <el-table-column prop="companyName" label="企业名称" width="150" show-overflow-tooltip />
        <el-table-column prop="companyShortName" label="简称" width="100" show-overflow-tooltip />
        <el-table-column prop="phone" label="联系电话" width="150" />
        <el-table-column label="公海回收(天)" width="130" align="center">
          <template #default="{ row }">
            <div class="public-sea-cell">
              <div>自建：{{ formatRecycleDays(row.publicSeaRecycleDays) }}</div>
              <div>认领：{{ formatRecycleDays(row.publicSeaRecycleDaysClaimed) }}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="70">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'danger'" size="small">
              {{ row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="配额信息" width="220">
          <template #default="{ row }">
            <div v-if="row.quota" class="quota-info">
              <div style="font-weight: bold; margin-bottom: 3px;">
                总计: {{ row.quota.usedTotalCount || 0 }}/{{ row.quota.totalQuota || 0 }}
              </div>
              <div>业务员: {{ row.quota.usedSalesmanCount || 0 }}/{{ row.quota.salesmanQuota || 0 }}</div>
              <div>管理员: {{ row.quota.usedManagerCount || 0 }}/{{ row.quota.managerQuota || 0 }}</div>
              <div>会计: {{ row.quota.usedAccountantCount || 0 }}/{{ row.quota.accountantQuota || 0 }}</div>
              <div>内勤: {{ row.quota.usedAssistantCount || 0 }}/{{ row.quota.assistantQuota || 0 }}</div>
            </div>
            <span v-else style="color: #999; font-size: 12px;">未配置</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="380" fixed="right">
          <template #default="{ row }">
            <el-button type="info" size="small" @click="handleViewDetail(row)">详情</el-button>
            <el-button type="primary" size="small" @click="handleEdit(row)">编辑</el-button>
            <el-button type="success" size="small" @click="handlePublicSeaConfig(row)">公海配置</el-button>
            <el-button type="warning" size="small" @click="handleManageQuota(row)">配额</el-button>
            <el-button
              :type="isQuotaValidationEnabled(row) ? 'danger' : 'success'"
              plain
              size="small"
              @click="handleToggleQuotaValidation(row)"
            >
              {{ isQuotaValidationEnabled(row) ? '移除额度校验' : '还原额度校验' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="queryParams.pageNum"
          v-model:page-size="queryParams.pageSize"
          :total="total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- 创建/编辑企业对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="700px"
      @close="handleDialogClose"
    >
      <el-form
        ref="companyFormRef"
        :model="companyForm"
        :rules="companyFormRules"
        label-width="120px"
      >
        <el-form-item label="企业编码" prop="companyCode">
          <el-input v-model="companyForm.companyCode" :disabled="isEdit" />
        </el-form-item>
        <el-form-item label="企业名称" prop="companyName">
          <el-input v-model="companyForm.companyName" />
        </el-form-item>
        <el-form-item label="企业简称">
          <el-input v-model="companyForm.companyShortName" />
        </el-form-item>
        <el-form-item label="统一社会信用代码">
          <el-input v-model="companyForm.creditCode" />
        </el-form-item>
        <el-form-item label="公司法人">
          <el-input v-model="companyForm.legalPerson" />
        </el-form-item>
        <el-form-item label="公司地址">
          <el-input v-model="companyForm.address" type="textarea" :rows="2" />
        </el-form-item>
        <el-form-item label="联系电话">
          <el-input v-model="companyForm.phone" />
        </el-form-item>
        <el-form-item label="邮箱">
          <el-input v-model="companyForm.email" />
        </el-form-item>
        <el-form-item label="公司Logo">
          <el-input v-model="companyForm.logo" placeholder="Logo URL" />
        </el-form-item>
        <el-form-item label="公司描述">
          <el-input v-model="companyForm.description" type="textarea" :rows="3" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="companyForm.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
        <template v-if="!isEdit">
          <el-form-item label="公海·自建/导入(天)">
            <el-input-number v-model="companyForm.publicSeaRecycleDays" :min="-1" :max="3650" style="width: 100%" />
            <div class="form-hint">适用于个人新建、导入的客户；-1 不自动回收；≥1 按未跟进天数回收至公海（由 sys_scheduled_task 中 CRM_PUBLIC_SEA_AUTO_RECYCLE 任务触发扫描）</div>
          </el-form-item>
          <el-form-item label="公海·认领客户(天)">
            <el-input-number v-model="companyForm.publicSeaRecycleDaysClaimed" :min="-1" :max="3650" style="width: 100%" />
            <div class="form-hint">适用于从公海池认领后的客户；规则同上，可与自建分开配置</div>
          </el-form-item>
        </template>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit">确定</el-button>
      </template>
    </el-dialog>

    <!-- 公海回收规则（已存在企业） -->
    <el-dialog
      v-model="publicSeaDialogVisible"
      :title="publicSeaDialogTitle"
      width="520px"
      @close="handlePublicSeaDialogClose"
    >
      <el-alert type="warning" :closable="false" show-icon class="mb-12">
        此处仅保存本企业的天数阈值；任务是否启用及 Cron 请在「定时任务管理」维护（默认任务编码 CRM_PUBLIC_SEA_AUTO_RECYCLE）。
      </el-alert>
      <el-form label-width="150px">
        <el-form-item label="自建/导入(天)">
          <el-input-number v-model="publicSeaForm.publicSeaRecycleDays" :min="-1" :max="3650" style="width: 100%" />
          <div class="form-hint">个人新建、导入的客户；未跟进超过该天数回公海</div>
        </el-form-item>
        <el-form-item label="公海认领(天)">
          <el-input-number v-model="publicSeaForm.publicSeaRecycleDaysClaimed" :min="-1" :max="3650" style="width: 100%" />
          <div class="form-hint">从公海认领后的客户；可与自建使用不同天数</div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="publicSeaDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="publicSeaSaving" @click="submitPublicSeaConfig">保存</el-button>
      </template>
    </el-dialog>

    <!-- 企业详情对话框 -->
    <el-dialog
      v-model="detailDialogVisible"
      title="企业详情"
      width="700px"
    >
      <el-descriptions :column="2" border v-if="companyDetail">
        <el-descriptions-item label="企业ID">{{ companyDetail.id }}</el-descriptions-item>
        <el-descriptions-item label="企业编码">{{ companyDetail.companyCode }}</el-descriptions-item>
        <el-descriptions-item label="企业名称">{{ companyDetail.companyName }}</el-descriptions-item>
        <el-descriptions-item label="企业简称">{{ companyDetail.companyShortName || '-' }}</el-descriptions-item>
        <el-descriptions-item label="统一社会信用代码">{{ companyDetail.creditCode || '-' }}</el-descriptions-item>
        <el-descriptions-item label="公司法人">{{ companyDetail.legalPerson || '-' }}</el-descriptions-item>
        <el-descriptions-item label="公司地址" :span="2">{{ companyDetail.address || '-' }}</el-descriptions-item>
        <el-descriptions-item label="联系电话">{{ companyDetail.phone || '-' }}</el-descriptions-item>
        <el-descriptions-item label="邮箱">{{ companyDetail.email || '-' }}</el-descriptions-item>
        <el-descriptions-item label="公司Logo" :span="2">
          <el-image
            v-if="companyDetail.logo"
            :src="companyDetail.logo"
            style="width: 100px; height: 100px"
            fit="contain"
            :preview-src-list="[companyDetail.logo]"
            crossorigin="anonymous"
            referrerpolicy="no-referrer-when-downgrade"
          />
          <span v-else>-</span>
        </el-descriptions-item>
        <el-descriptions-item label="公司描述" :span="2">{{ companyDetail.description || '-' }}</el-descriptions-item>
        <el-descriptions-item label="状态">
          <el-tag :type="companyDetail.status === 1 ? 'success' : 'danger'">
            {{ companyDetail.status === 1 ? '启用' : '禁用' }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="公海·自建/导入(天)">
          {{ formatRecycleDaysDetail(companyDetail.publicSeaRecycleDays) }}
        </el-descriptions-item>
        <el-descriptions-item label="公海·认领客户(天)">
          {{ formatRecycleDaysDetail(companyDetail.publicSeaRecycleDaysClaimed) }}
        </el-descriptions-item>
        <el-descriptions-item label="创建时间">{{ formatDateTime(companyDetail.createTime) }}</el-descriptions-item>
        <el-descriptions-item label="更新时间">{{ formatDateTime(companyDetail.updateTime) }}</el-descriptions-item>
        <el-descriptions-item label="配额信息" :span="2" v-if="companyDetail.quota">
          <div class="quota-detail">
            <div style="font-weight: bold; margin-bottom: 5px; font-size: 16px;">
              <strong>总配额：</strong>{{ companyDetail.quota.usedTotalCount || 0 }}/{{ companyDetail.quota.totalQuota || 0 }}
            </div>
            <div><strong>业务员：</strong>{{ companyDetail.quota.usedSalesmanCount || 0 }}/{{ companyDetail.quota.salesmanQuota || 0 }}</div>
            <div><strong>管理员：</strong>{{ companyDetail.quota.usedManagerCount || 0 }}/{{ companyDetail.quota.managerQuota || 0 }}</div>
            <div><strong>会计：</strong>{{ companyDetail.quota.usedAccountantCount || 0 }}/{{ companyDetail.quota.accountantQuota || 0 }}</div>
            <div><strong>内勤：</strong>{{ companyDetail.quota.usedAssistantCount || 0 }}/{{ companyDetail.quota.assistantQuota || 0 }}</div>
            <div v-if="companyDetail.quota.allowedRoleTypes && companyDetail.quota.allowedRoleTypes.length > 0">
              <strong>允许的角色：</strong>{{ companyDetail.quota.allowedRoleTypes.join('、') }}
            </div>
            <div v-else><strong>允许的角色：</strong>全部</div>
          </div>
        </el-descriptions-item>
        <el-descriptions-item label="配额信息" :span="2" v-else>
          <span style="color: #999;">未配置</span>
        </el-descriptions-item>
      </el-descriptions>
      <template #footer>
        <el-button type="primary" @click="detailDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>

    <!-- 配额管理对话框 -->
    <el-dialog
      v-model="quotaDialogVisible"
      title="配额管理"
      width="600px"
      @close="handleQuotaDialogClose"
    >
      <el-form
        ref="quotaFormRef"
        :model="quotaForm"
        label-width="150px"
      >
        <el-divider content-position="left">总配额</el-divider>
        <el-form-item label="总账号配额">
          <el-input-number v-model="quotaForm.totalQuota" :min="0" style="width: 100%" />
          <div style="margin-top: 5px; color: #999; font-size: 12px;">
            提示：总配额是企业可以创建的总账号数，企业可以自行分配到不同角色
          </div>
        </el-form-item>
        <el-form-item label="已使用总数">
          <el-input :value="currentQuota?.usedTotalCount || 0" disabled style="width: 100%" />
        </el-form-item>
        <el-divider content-position="left">角色配额（可选）</el-divider>
        <el-form-item label="业务员账号配额">
          <el-input-number v-model="quotaForm.salesmanQuota" :min="0" style="width: 100%" />
          <div style="margin-top: 5px; color: #999; font-size: 12px;">
            提示：角色配额为可选配置，用于限制单个角色的最大账号数
          </div>
        </el-form-item>
        <el-form-item label="管理员账号配额">
          <el-input-number v-model="quotaForm.managerQuota" :min="0" style="width: 100%" />
        </el-form-item>
        <el-form-item label="会计账号配额">
          <el-input-number v-model="quotaForm.accountantQuota" :min="0" style="width: 100%" />
        </el-form-item>
        <el-form-item label="内勤账号配额">
          <el-input-number v-model="quotaForm.assistantQuota" :min="0" style="width: 100%" />
        </el-form-item>
        <el-divider content-position="left">已使用数量</el-divider>
        <el-form-item label="业务员已使用">
          <el-input :value="currentQuota?.usedSalesmanCount || 0" disabled style="width: 100%" />
        </el-form-item>
        <el-form-item label="管理员已使用">
          <el-input :value="currentQuota?.usedManagerCount || 0" disabled style="width: 100%" />
        </el-form-item>
        <el-form-item label="会计已使用">
          <el-input :value="currentQuota?.usedAccountantCount || 0" disabled style="width: 100%" />
        </el-form-item>
        <el-form-item label="内勤已使用">
          <el-input :value="currentQuota?.usedAssistantCount || 0" disabled style="width: 100%" />
        </el-form-item>
        <el-divider content-position="left">允许的角色类型</el-divider>
        <el-form-item label="允许的角色">
          <el-checkbox-group v-model="quotaForm.allowedRoleTypes">
            <el-checkbox label="SALESMAN">业务员</el-checkbox>
            <el-checkbox label="MANAGER">管理员</el-checkbox>
            <el-checkbox label="ACCOUNTANT">会计</el-checkbox>
            <el-checkbox label="ASSISTANT">内勤</el-checkbox>
          </el-checkbox-group>
          <div style="margin-top: 10px; color: #999; font-size: 12px;">
            提示：不选择任何角色表示允许所有角色
          </div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="quotaDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleQuotaSubmit">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  getCompanyList,
  getCompanyById,
  createCompany,
  updateCompany,
  getCompanyQuota,
  updateCompanyQuota
} from '../api/company'

const router = useRouter()

const goScheduledTaskPage = () => {
  router.push('/home/scheduled-task')
}

// 数据
const loading = ref(false)
const companyList = ref([])
const total = ref(0)
const queryParams = reactive({
  companyName: '',
  companyCode: '',
  status: null,
  pageNum: 1,
  pageSize: 10
})

// 对话框
const dialogVisible = ref(false)
const dialogTitle = ref('新增企业')
const isEdit = ref(false)
const companyFormRef = ref(null)
const companyForm = reactive({
  id: null,
  companyCode: '',
  companyName: '',
  companyShortName: '',
  creditCode: '',
  legalPerson: '',
  address: '',
  phone: '',
  email: '',
  logo: '',
  description: '',
  status: 1,
  publicSeaRecycleDays: -1,
  publicSeaRecycleDaysClaimed: -1
})

const companyFormRules = {
  companyCode: [{ required: true, message: '请输入企业编码', trigger: 'blur' }],
  companyName: [{ required: true, message: '请输入企业名称', trigger: 'blur' }],
  status: [{ required: true, message: '请选择状态', trigger: 'change' }]
}

// 详情对话框
const detailDialogVisible = ref(false)
const companyDetail = ref(null)

// 配额管理对话框
const quotaDialogVisible = ref(false)
const quotaFormRef = ref(null)
const currentCompanyId = ref(null)
const currentQuota = ref(null)
const quotaForm = reactive({
  totalQuota: 0,
  salesmanQuota: 0,
  managerQuota: 0,
  accountantQuota: 0,
  assistantQuota: 0,
  allowedRoleTypes: []
})

const publicSeaDialogVisible = ref(false)
const publicSeaDialogTitle = ref('公海配置')
const publicSeaSaving = ref(false)
const publicSeaSnapshot = ref(null)
const publicSeaForm = reactive({
  publicSeaRecycleDays: -1,
  publicSeaRecycleDaysClaimed: -1
})

// 方法
const formatRecycleDays = (v) => {
  if (v == null || v < 0) return '关'
  return String(v)
}

const formatRecycleDaysDetail = (v) => {
  if (v == null || v < 0) return '不自动回收（-1）'
  return String(v)
}

const isQuotaValidationEnabled = (row) => {
  const enabled = row?.quota?.quotaValidationEnabled
  return enabled == null ? true : enabled === 1
}

const formatDateTime = (dateTime) => {
  if (!dateTime) return '-'
  const date = new Date(dateTime)
  return date.toLocaleString('zh-CN')
}

const loadCompanyList = async () => {
  loading.value = true
  try {
    const res = await getCompanyList(queryParams)
    if (res.code === '000000') {
      companyList.value = res.data.records || []
      total.value = res.data.total || 0
    }
  } catch (error) {
    console.error('获取企业列表失败:', error)
    ElMessage.error('获取企业列表失败')
  } finally {
    loading.value = false
  }
}

const handleSearch = () => {
  queryParams.pageNum = 1
  loadCompanyList()
}

const handleReset = () => {
  queryParams.companyName = ''
  queryParams.companyCode = ''
  queryParams.status = null
  queryParams.pageNum = 1
  loadCompanyList()
}

const handleCreate = () => {
  dialogTitle.value = '新增企业'
  isEdit.value = false
  Object.assign(companyForm, {
    id: null,
    companyCode: '',
    companyName: '',
    companyShortName: '',
    creditCode: '',
    legalPerson: '',
    address: '',
    phone: '',
    email: '',
    logo: '',
    description: '',
    status: 1,
    publicSeaRecycleDays: -1,
    publicSeaRecycleDaysClaimed: -1
  })
  dialogVisible.value = true
}

const handlePublicSeaConfig = async (row) => {
  try {
    const res = await getCompanyById(row.id)
    if (res.code !== '000000') {
      ElMessage.error(res.message || '获取企业失败')
      return
    }
    publicSeaSnapshot.value = JSON.parse(JSON.stringify(res.data))
    publicSeaForm.publicSeaRecycleDays = res.data.publicSeaRecycleDays ?? -1
    publicSeaForm.publicSeaRecycleDaysClaimed = res.data.publicSeaRecycleDaysClaimed ?? -1
    publicSeaDialogTitle.value = `公海配置 — ${res.data.companyName || row.id}`
    publicSeaDialogVisible.value = true
  } catch (error) {
    console.error('加载公海配置失败:', error)
    ElMessage.error('加载公海配置失败')
  }
}

const handlePublicSeaDialogClose = () => {
  publicSeaSnapshot.value = null
}

const submitPublicSeaConfig = async () => {
  if (!publicSeaSnapshot.value) return
  publicSeaSaving.value = true
  try {
    const payload = {
      ...publicSeaSnapshot.value,
      publicSeaRecycleDays: publicSeaForm.publicSeaRecycleDays,
      publicSeaRecycleDaysClaimed: publicSeaForm.publicSeaRecycleDaysClaimed
    }
    const res = await updateCompany(payload)
    if (res.code === '000000') {
      ElMessage.success('公海规则已保存')
      publicSeaDialogVisible.value = false
      loadCompanyList()
    } else {
      ElMessage.error(res.message || '保存失败')
    }
  } catch (error) {
    console.error('保存公海配置失败:', error)
    ElMessage.error('保存公海配置失败')
  } finally {
    publicSeaSaving.value = false
  }
}

const handleEdit = async (row) => {
  dialogTitle.value = '编辑企业'
  isEdit.value = true
  try {
    const res = await getCompanyById(row.id)
    if (res.code === '000000') {
      Object.assign(companyForm, res.data)
    }
    dialogVisible.value = true
  } catch (error) {
    console.error('获取企业详情失败:', error)
    ElMessage.error('获取企业详情失败')
  }
}

const handleSubmit = async () => {
  if (!companyFormRef.value) return
  await companyFormRef.value.validate(async (valid) => {
    if (valid) {
      try {
        const res = isEdit.value
          ? await updateCompany({ ...companyForm })
          : await createCompany({ ...companyForm })
        if (res.code === '000000') {
          ElMessage.success(isEdit.value ? '更新成功' : '创建成功')
          dialogVisible.value = false
          loadCompanyList()
        } else {
          ElMessage.error(res.message || '保存失败')
        }
      } catch (error) {
        console.error('保存企业失败:', error)
        ElMessage.error('保存企业失败：' + (error.message || '未知错误'))
      }
    }
  })
}

const handleViewDetail = async (row) => {
  try {
    const res = await getCompanyById(row.id)
    if (res.code === '000000') {
      companyDetail.value = res.data
      detailDialogVisible.value = true
    } else {
      ElMessage.error(res.message || '获取企业详情失败')
    }
  } catch (error) {
    console.error('获取企业详情失败:', error)
    ElMessage.error('获取企业详情失败：' + (error.message || '未知错误'))
  }
}


const handleManageQuota = async (row) => {
  currentCompanyId.value = row.id
  try {
    const res = await getCompanyQuota(row.id)
    if (res.code === '000000') {
      currentQuota.value = res.data
      quotaForm.totalQuota = res.data.totalQuota || 0
      quotaForm.salesmanQuota = res.data.salesmanQuota || 0
      quotaForm.managerQuota = res.data.managerQuota || 0
      quotaForm.accountantQuota = res.data.accountantQuota || 0
      quotaForm.assistantQuota = res.data.assistantQuota || 0
      quotaForm.allowedRoleTypes = (res.data.allowedRoleTypes || []).filter(role => role !== 'SYSTEM_ADMIN')
    } else {
      // 如果没有配额配置，使用默认值
      currentQuota.value = {
        usedSalesmanCount: 0,
        usedManagerCount: 0,
        usedAccountantCount: 0,
        usedAssistantCount: 0,
        usedSystemAdminCount: 0
      }
      quotaForm.salesmanQuota = 0
      quotaForm.managerQuota = 0
      quotaForm.accountantQuota = 0
      quotaForm.assistantQuota = 0
      quotaForm.systemAdminQuota = 0
      quotaForm.allowedRoleTypes = []
    }
    quotaDialogVisible.value = true
  } catch (error) {
    console.error('获取配额信息失败:', error)
    ElMessage.error('获取配额信息失败')
  }
}

const handleQuotaSubmit = async () => {
  try {
    const res = await updateCompanyQuota(currentCompanyId.value, { ...quotaForm })
    if (res.code === '000000') {
      ElMessage.success('配额更新成功')
      quotaDialogVisible.value = false
      loadCompanyList()
    } else {
      ElMessage.error(res.message || '配额更新失败')
    }
  } catch (error) {
    console.error('更新配额失败:', error)
    ElMessage.error('更新配额失败：' + (error.message || '未知错误'))
  }
}

const handleToggleQuotaValidation = async (row) => {
  const currentlyEnabled = isQuotaValidationEnabled(row)
  const nextEnabled = currentlyEnabled ? 0 : 1
  const actionText = currentlyEnabled ? '移除额度校验' : '还原额度校验'
  const successText = currentlyEnabled ? '已移除账号额度校验' : '已还原账号额度校验'

  try {
    await ElMessageBox.confirm(
      currentlyEnabled
        ? `确定移除企业「${row.companyName || row.id}」的账号额度校验吗？移除后该企业创建账号将不再受总配额与角色配额限制。`
        : `确定还原企业「${row.companyName || row.id}」的账号额度校验吗？还原后该企业会继续按已配置的总配额与角色配额进行校验。`,
      '确认操作',
      {
        confirmButtonText: actionText,
        cancelButtonText: '取消',
        type: 'warning'
      }
    )

    let targetQuota = row.quota
    if (!targetQuota) {
      const quotaRes = await getCompanyQuota(row.id)
      if (quotaRes.code !== '000000') {
        ElMessage.error(quotaRes.message || '获取配额失败')
        return
      }
      targetQuota = quotaRes.data
    }

    const payload = {
      totalQuota: targetQuota?.totalQuota ?? 0,
      salesmanQuota: targetQuota?.salesmanQuota ?? 0,
      managerQuota: targetQuota?.managerQuota ?? 0,
      accountantQuota: targetQuota?.accountantQuota ?? 0,
      assistantQuota: targetQuota?.assistantQuota ?? 0,
      allowedRoleTypes: targetQuota?.allowedRoleTypes || [],
      quotaValidationEnabled: nextEnabled
    }
    const res = await updateCompanyQuota(row.id, payload)
    if (res.code === '000000') {
      ElMessage.success(successText)
      loadCompanyList()
    } else {
      ElMessage.error(res.message || `${actionText}失败`)
    }
  } catch (error) {
    if (error === 'cancel') return
    console.error('切换额度校验失败:', error)
    ElMessage.error(`${actionText}失败`)
  }
}

const handleDialogClose = () => {
  companyFormRef.value?.resetFields()
}

const handleQuotaDialogClose = () => {
  quotaFormRef.value?.resetFields()
}

const handleSizeChange = (size) => {
  queryParams.pageSize = size
  loadCompanyList()
}

const handlePageChange = (page) => {
  queryParams.pageNum = page
  loadCompanyList()
}

// 生命周期
onMounted(() => {
  loadCompanyList()
})
</script>

<style scoped>
.company-management {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.company-sea-alert {
  margin-bottom: 16px;
}

.company-sea-alert-body {
  font-size: 13px;
  line-height: 1.6;
}

.company-sea-alert-body code {
  margin: 0 2px;
}

.mb-12 {
  margin-bottom: 12px;
}

.search-form {
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.quota-info {
  font-size: 12px;
  line-height: 1.5;
}

.quota-info div {
  margin: 2px 0;
}

.quota-detail {
  font-size: 14px;
  line-height: 2;
}

.public-sea-cell {
  font-size: 12px;
  line-height: 1.45;
  text-align: left;
  padding: 2px 0;
}

.form-hint {
  margin-top: 6px;
  color: #999;
  font-size: 12px;
  line-height: 1.5;
}

.quota-detail div {
  margin: 5px 0;
}
</style>

